"use client";

import React, { useState, useEffect, useCallback } from "react";

import BasicInputs from "./BasicInputs";
import AdvancedInputs from "./AdvancedInputs";
import MortgageResult from "./MortgageResult";

const HomeToolsPayment = () => {
    const [loanAmount, setLoanAmount] = useState<number>(0);
    const [loanTerm, setLoanTerm] = useState<number>(0);
    const [mortgageRate, setMortgageRate] = useState<number>(0);
    const [propertyTax, setPropertyTax] = useState<number>(0);
    const [insurance, setInsurance] = useState<number>(0);
    const [monthlyBreakdown, setMonthlyBreakdown] = useState<{
        principalAndInterest: number;
        taxesAndInsurance: number;
        total: number;
    } | null>(null);

    const [resultOpen, setResultOpen] = useState(false);

    useEffect(() => {
        if (mortgageRate > 0) {
            setResultOpen(true);
        }
    }, [mortgageRate]);

    const toCurrency = (num: number) => {
        if (!isFinite(num) || isNaN(num)) return "$0.00";
        return "$" + num.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, "$1,");
    };

    const handleCalculate = useCallback(() => {
        const months = loanTerm * 12;
        const monthlyRate = mortgageRate / 100 / 12;

        let principalAndInterest;
        if (monthlyRate === 0) {
            principalAndInterest = loanAmount / months;
        } else {
            const factor = Math.pow(1 + monthlyRate, months);
            principalAndInterest = (loanAmount * monthlyRate * factor) / (factor - 1);
        }

        const monthlyTax = propertyTax / 12;
        const monthlyInsurance = insurance / 12;
        const taxesAndInsurance = monthlyTax + monthlyInsurance;
        const total = principalAndInterest + taxesAndInsurance;

        setMonthlyBreakdown({
            principalAndInterest,
            taxesAndInsurance,
            total,
        });
    }, [loanAmount, loanTerm, mortgageRate, propertyTax, insurance]);

    useEffect(() => {
        handleCalculate();
    }, [handleCalculate]);

    return (
        <div className="2xl:py-10 2xl:px-4 2xl:pb-60" aria-labelledby="page-title">
            <div className="max-w-3xl mx-auto 2xl:pl-[28px]">
                <div className="text-center mb-4 2xl:w-[900px]">
                    <h1 id="page-title" className="text-2xl md:text-3xl font-bold uppercase text-[#212459] mb-2">
                        Mortgage Payment Calculator
                    </h1>
                    <h2 className="text-lg md:text-xl font-semibold uppercase text-[#212459]">
                        Check Your Monthly Payment
                    </h2>
                </div>

                <div className="pb-[30px] w-[350px] mx-auto 2xl:w-[1200px] 2xl:mx-0">
                    {/* BASIC Section */}
                    <BasicInputs
                        loanAmount={loanAmount}
                        setLoanAmount={setLoanAmount}
                        loanTerm={loanTerm}
                        setLoanTerm={setLoanTerm}
                        mortgageRate={mortgageRate}
                        setMortgageRate={setMortgageRate}
                    />

                    {/* ADVANCED Section */}
                    <AdvancedInputs
                        propertyTax={propertyTax}
                        setPropertyTax={setPropertyTax}
                        insurance={insurance}
                        setInsurance={setInsurance}
                    />

                    {/* Result Display */}
                    {monthlyBreakdown && (
                        <MortgageResult
                            total={monthlyBreakdown.total}
                            principalAndInterest={monthlyBreakdown.principalAndInterest}
                            taxesAndInsurance={monthlyBreakdown.taxesAndInsurance}
                            loanAmount={loanAmount}
                            annualRate={mortgageRate}
                            loanTerm={loanTerm}
                            toCurrency={toCurrency}
                            isOpen={resultOpen}
                            setIsOpen={setResultOpen}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default HomeToolsPayment;
